/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { useWindowSize } from '@slr/shared-library';
import {
  MOBILE_VIEW_WIDTH,
  PROFILE_ORGANIZATIONS_PATH,
  PROFILE_PATH
} from 'configs';
import { useGetCurrentUserProfileWithSolution1 } from 'hooks';
import { FC, useMemo } from 'react';
import { ButtonGroup, Container, Dropdown, NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { getProfileNavItems } from '../../pages/profile/profileNavItems';
import './ProfileMobileDropdown.scss';

export const ProfileMobileDropdown: FC = () => {
  const { isMobileView } = useWindowSize(MOBILE_VIEW_WIDTH);
  const { pathname } = useLocation();
  const { t } = useTranslation('translation', {
    keyPrefix: 'profilePage'
  });
  const { data: currentUserProfile } = useGetCurrentUserProfileWithSolution1();

  const navItems = useMemo(
    () => getProfileNavItems(t, currentUserProfile),
    [t, currentUserProfile]
  );

  const title = useMemo(() => {
    // Handle special case for organizations path - match /profile/organizations and /profile/organizations/:id
    if (pathname.startsWith(PROFILE_ORGANIZATIONS_PATH)) {
      return (
        navItems.find((item) => item.to === PROFILE_ORGANIZATIONS_PATH)
          ?.label ?? ''
      );
    }
    // For other paths, use exact match
    return navItems.find((item) => pathname === item.to)?.label ?? '';
  }, [navItems, pathname]);

  // Only show on profile-related routes and on mobile
  if (!isMobileView || !pathname.startsWith(PROFILE_PATH)) {
    return null;
  }

  return (
    <div className="profile-mobile-dropdown">
      <Container>
        <Dropdown as={ButtonGroup} className="profile-drop-down">
          <Dropdown.Toggle
            variant="outline-primary"
            className="mobile-dropdown-toggle"
          >
            {title}
          </Dropdown.Toggle>
          <Dropdown.Toggle split variant="outline-primary" size="lg" />
          <Dropdown.Menu>
            {navItems.map((item) => (
              <NavDropdown.Item as={Link} key={item.key} to={item.to}>
                {item.label}
              </NavDropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Container>
    </div>
  );
};

export default ProfileMobileDropdown;
